/**
 * TopologyRenderer — AntV G6 高性能关系图谱渲染器
 *
 * 将 ```topology 代码块渲染为可交互的引力布局网络图。
 * 支持：物理弹簧布局、缩放平移、节点悬停高亮、拖拽。
 *
 * AI 输出 JSON 格式：
 * {
 *   "title": "股权穿透图",
 *   "layout": "force" | "dagre" | "circular",
 *   "nodes": [
 *     {"id": "1", "label": "A公司", "type": "company", "size": 40},
 *     ...
 *   ],
 *   "edges": [
 *     {"source": "1", "target": "2", "label": "持股51%"},
 *     ...
 *   ]
 * }
 */

import React, { useEffect, useRef, useState, useMemo, useId } from 'react';

// Institutional Minimalist 节点色系
const NODE_COLORS: Record<string, string> = {
    company: '#3B82F6',   // Blue
    person: '#6366F1',    // Indigo
    account: '#0EA5E9',   // Sky
    entity: '#14B8A6',    // Teal
    risk: '#EF4444',      // Red (风险节点)
    default: '#64748B',   // Slate
};

interface TopologyRendererProps {
    optionString: string;
}

const TopologyRenderer: React.FC<TopologyRendererProps> = ({ optionString }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const graphRef = useRef<any>(null);
    const [error, setError] = useState<string>('');
    const [nodeCount, setNodeCount] = useState(0);
    const uniqueId = useId().replace(/:/g, '_');

    const config = useMemo(() => {
        try {
            const parsed = JSON.parse(optionString.trim());
            setNodeCount((parsed.nodes || []).length);
            return parsed;
        } catch (err: any) {
            setError(err.message);
            return null;
        }
    }, [optionString]);

    useEffect(() => {
        if (!config || !containerRef.current) return;

        let graph: any = null;

        const initGraph = async () => {
            try {
                // Dynamic import to avoid loading G6 upfront
                const G6 = await import('@antv/g6');

                const nodes = (config.nodes || []).map((n: any) => ({
                    id: String(n.id),
                    data: {
                        label: n.label || n.id,
                        nodeType: n.type || 'default',
                    },
                    style: {
                        fill: NODE_COLORS[n.type] || NODE_COLORS.default,
                        stroke: '#E5E7EB',
                        lineWidth: 1,
                        r: n.size || 24,
                        labelText: n.label || n.id,
                        labelFontSize: 11,
                        labelFontFamily: 'Inter, "Noto Sans SC", sans-serif',
                        labelFill: '#1F2937',
                    },
                }));

                const edges = (config.edges || []).map((e: any, i: number) => ({
                    id: `edge-${i}`,
                    source: String(e.source),
                    target: String(e.target),
                    style: {
                        stroke: '#9CA3AF',
                        lineWidth: 1.5,
                        endArrow: true,
                        labelText: e.label || '',
                        labelFontSize: 10,
                        labelFill: '#6B7280',
                        labelBackground: true,
                        labelBackgroundFill: '#FAFAFA',
                        labelBackgroundOpacity: 0.9,
                        labelPadding: [2, 4],
                    },
                }));

                const layoutType = config.layout || 'force';
                const layoutConfig: any = layoutType === 'dagre'
                    ? { type: 'dagre', rankdir: 'TB', nodesep: 60, ranksep: 80 }
                    : layoutType === 'circular'
                        ? { type: 'circular', radius: 200 }
                        : { type: 'force', preventOverlap: true, nodeSpacing: 80, linkDistance: 150 };

                graph = new G6.Graph({
                    container: containerRef.current!,
                    width: containerRef.current!.clientWidth,
                    height: 420,
                    data: { nodes, edges },
                    layout: layoutConfig,
                    behaviors: ['drag-canvas', 'zoom-canvas', 'drag-element'],
                    animation: true,
                });

                graphRef.current = graph;
                await graph.render();
            } catch (err: any) {
                console.error('[Topology] G6 render failed:', err);
                setError(err.message);
            }
        };

        initGraph();

        return () => {
            if (graphRef.current) {
                graphRef.current.destroy();
                graphRef.current = null;
            }
        };
    }, [config]);

    if (error) {
        return (
            <div style={{
                margin: '12px 0', padding: '16px', background: '#F9FAFB',
                border: '1px solid #E5E7EB', borderRadius: '8px', position: 'relative',
            }}>
                <div style={{ position: 'absolute', top: '8px', right: '12px', fontSize: '11px', color: '#9CA3AF', fontWeight: 600 }}>
                    TOPOLOGY
                </div>
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: '13px', color: '#374151', fontFamily: 'monospace' }}>
                    {optionString}
                </pre>
            </div>
        );
    }

    return (
        <div style={{
            margin: '16px 0', border: '1px solid #E5E7EB', borderRadius: '12px',
            overflow: 'hidden', background: '#FAFAFA', position: 'relative',
        }}>
            {/* 标题栏 */}
            <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '8px 12px', background: '#F3F4F6', borderBottom: '1px solid #E5E7EB',
            }}>
                <div style={{ fontSize: '13px', color: '#1F2937', fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>
                    {config?.title || '关系图谱'}
                </div>
                <div style={{ fontSize: '11px', color: '#9CA3AF', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#8B5CF6', display: 'inline-block' }} />
                    TOPOLOGY · {nodeCount} 节点
                </div>
            </div>
            {/* G6 Canvas */}
            <div
                id={`topology-${uniqueId}`}
                ref={containerRef}
                style={{ width: '100%', height: '420px' }}
            />
        </div>
    );
};

export default TopologyRenderer;
